import { isNative } from './platform';

/**
 * Talks to the Worker. On the web the app and API share an origin and a session cookie.
 * The Android app keeps the server address and a device token from pairing.
 */

interface ApiConfig {
  base: string;
  token: string | null;
}

const config: ApiConfig = { base: '', token: null };
const signedOutListeners = new Set<() => void>();

export function configureApi(next: Partial<ApiConfig>) {
  Object.assign(config, next);
}

export function apiBase(): string {
  return config.base || (typeof location !== 'undefined' ? location.origin : '');
}

export function onSignedOut(fn: () => void) {
  signedOutListeners.add(fn);
  return () => signedOutListeners.delete(fn);
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** True when a failure means "no connection" rather than an error from the server. */
export function isOffline(err: unknown): boolean {
  return err instanceof ApiError ? err.status === 0 : true;
}

export function authHeaders(): Record<string, string> {
  return isNative && config.token ? { authorization: `Bearer ${config.token}` } : {};
}

export async function api<T>(
  path: string,
  init: {
    method?: string;
    body?: unknown;
    signal?: AbortSignal;
    raw?: BodyInit;
    contentType?: string;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = { ...authHeaders() };
  let body: BodyInit | undefined;
  if (init.raw !== undefined) {
    body = init.raw;
    if (init.contentType) headers['content-type'] = init.contentType;
  } else if (init.body !== undefined) {
    body = JSON.stringify(init.body);
    headers['content-type'] = 'application/json';
  }
  let res: Response;
  try {
    res = await fetch(`${config.base}${path}`, {
      method: init.method ?? (body !== undefined ? 'POST' : 'GET'),
      headers,
      body,
      signal: init.signal,
      credentials: isNative ? 'omit' : 'same-origin',
    });
  } catch {
    throw new ApiError(
      "You're offline. Changes are saved and will sync when you're back online.",
      0,
    );
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    if (res.status === 401) for (const fn of signedOutListeners) fn();
    const fromServer = (data as { error?: unknown } | null)?.error;
    const message =
      typeof fromServer === 'string' && fromServer
        ? fromServer
        : 'Something went wrong. Try again in a moment.';
    throw new ApiError(message, res.status);
  }
  return data as T;
}

/** WebSocket URL for an API path. */
export function socketUrl(path: string): string {
  const url = new URL(path, apiBase());
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

/** Fetches a file with the right credentials and returns it as a blob. */
export async function fetchBlob(path: string): Promise<Blob> {
  const res = await fetch(`${config.base}${path}`, {
    headers: authHeaders(),
    credentials: isNative ? 'omit' : 'same-origin',
  });
  if (!res.ok) throw new ApiError("That file isn't available yet.", res.status);
  return res.blob();
}
